/**
 * utils/pdfPageRasterizer.ts
 *
 * Rasterises every page of a PDF buffer to JPEG bytes using Mozilla's pdf.js
 * (pdfjs-dist). This is the definitive fallback for PDFs that pdf-lib cannot
 * process: owner-password-encrypted files, proprietary compression, exotic
 * filter chains, etc.
 *
 * pdf.js performs full decryption and rendering just like a real PDF viewer,
 * so ANY PDF that opens in a browser will be correctly rendered here.
 *
 * The worker path is resolved at build time via Vite's `new URL(…, import.meta.url)`
 * static-asset mechanism so the worker .mjs file is included in the dist bundle
 * without any additional Vite plugin configuration.
 *
 * BROWSER-ONLY: requires HTMLCanvasElement and a browser pdf.js worker.
 *
 * Created Jun 2026.
 */

import type * as PdfjsType from 'pdfjs-dist';

let pdfjsLib: typeof PdfjsType | null = null;

async function getPdfjsLib(): Promise<typeof PdfjsType> {
  if (pdfjsLib) return pdfjsLib;

  const lib = await import('pdfjs-dist');

  // Resolve the worker URL at runtime using Vite's static asset mechanism.
  // new URL(…, import.meta.url) is handled by Vite as a direct asset reference:
  // the file is copied to dist and the URL points to the versioned output path.
  const workerUrl = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).href;

  lib.GlobalWorkerOptions.workerSrc = workerUrl;
  pdfjsLib = lib;
  return lib;
}

/**
 * Renders all pages of `pdfBuffer` to JPEG Uint8Arrays at the given DPI
 * (default 200). Returns one entry per page in document order.
 *
 * Throws if pdf.js cannot load the document at all (truly unreadable PDF).
 */
export async function rasterizePdfToJpegs(
  pdfBuffer: ArrayBuffer,
  dpi = 200,
): Promise<Uint8Array[]> {
  const lib = await getPdfjsLib();

  // Defensive copy: do not transfer the caller's ArrayBuffer.
  const data = new Uint8Array(pdfBuffer.slice(0));

  const pdfDoc = await lib.getDocument({
    data,
    // Empty user password handles owner-only-protected PDFs (most common case
    // for French administrative documents like Kbis, actes, documents CAF…).
    password: '',
  }).promise;

  const scale = dpi / 72; // 1 PDF point = 1/72 inch
  const results: Uint8Array[] = [];

  for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    await page.render({ canvas, viewport }).promise;

    const jpegBytes = await new Promise<Uint8Array>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('rasterizePdfToJpegs: toBlob returned null'));
            return;
          }
          blob.arrayBuffer().then((ab) => resolve(new Uint8Array(ab))).catch(reject);
        },
        'image/jpeg',
        0.92,
      );
    });

    results.push(jpegBytes);
    page.cleanup();
  }

  await pdfDoc.cleanup();
  return results;
}
